import {ReactElement, useEffect, useState} from "react";
import {
    AffordableUtsvMonthValueInput,
    GetAffordableConventionalUtsvQuery,
    useGetAffordableConventionalUtsvLazyQuery,
    useGetForecastLocksLazyQuery, useUpsertAffordableUtsvDataMutation,
    VersionType
} from "../../../../../__generated__/generated_types";
import {useProperties} from "../../../../../contexts/properties/PropertiesContext";
import {IVZHotTableRow} from "../../../../../components/vz-ui/vz-hot-table/VZHotTable";
import {ViziblyTheme} from "../../../../../styles/zendesk-garden/ViziblyZDGTheme";
import WorkflowHeader from "../../../../../components/workflows/workflow-header/WorkflowHeader";
import WorkflowNavToggle from "../../../../../components/workflows/workflow-nav-toggle/WorkflowNavToggle";
import WorkflowPageLabel from "../../../../../components/workflows/workflow-page-label/WorkflowPageLabel";
import VZSaveStatus from "../../../../../components/vz-ui/vz-save-status/VZSaveStatus";
import WorkflowStepNav from "../../../../../components/workflows/workflow-step-nav/WorkflowStepNav";
import {Tabs} from "@zendeskgarden/react-tabs";
import WorkflowYearTabs, {
    WORKFLOW_YEAR_TAB_BUDGET
} from "../../../../../components/workflows/workflow-year-tabs/WorkflowYearTabs";
import OperationalVZHotTable from "../../OperationalVZHotTable";
import {getMonths} from "../../../../../utils/months-helper";
import {ThemeProvider} from "styled-components";
import * as workflowCSS from "../../../../../styles/workflows/workflowCSS.module.scss";
import * as workflowHeaderCSS from "../../../../../components/workflows/workflow-header/styles/css.module.scss";
import {BudgetingType} from "../../../../../BudgetingType";
import {shouldUpdateWorkflowTab} from "../../../logic/workflows";
import {useParams} from "react-router-dom";
import {VZHotTableConfig} from "../../../../../components/vz-ui/vz-hot-table/vz-hot-table-config";
import {buildUTSVTableConfig} from "../../utils/operational-metric-helpers";

type UtsvFields = keyof Pick<(GetAffordableConventionalUtsvQuery["affordableUTSVData"][0]), "moveInCount" | "affordableModelSubsidizedMoveInCount" | "affordableModelSubsidizedMoveOutCount" | "affordableModelAffordableUnitAvgMaxRent">;

export interface AffordableConventionalProps {
    budgetYear: number;
    onPreviousClick: () => void;
    onNextClick: () => void;
    field: UtsvFields;
    format: "currency" | "count";
    pageName: string;
}

/**
 * I'm using this only for a single UTSV value at this point for expediency.
 */

export function AffordableGeneric(props: AffordableConventionalProps): ReactElement {
    const {type} = useParams() as { type: BudgetingType; };
    const {currentProperty} = useProperties();
    const [getForecastLocks, {data: forecastLocks}] = useGetForecastLocksLazyQuery({
        fetchPolicy: "no-cache"
    });
    const [getAffordableUtsvData, {data, loading}] = useGetAffordableConventionalUtsvLazyQuery({
        fetchPolicy: "no-cache"
    });
    const [upsertAffordableUtsvData, {loading: upsertAffordableUtsvDataLoading}] = useUpsertAffordableUtsvDataMutation({
        notifyOnNetworkStatusChange: true
    });
    const [selectedTab, setSelectedTab] = useState(WORKFLOW_YEAR_TAB_BUDGET);
    const [tableData, setTableData] = useState<IVZHotTableRow[]>([]);
    const [tableConfig, setTableConfig] = useState<VZHotTableConfig | undefined>(undefined);
    const [savingValues, setSavingValues] = useState<number>(0);
    const [lastSaved, setLastSaved] = useState<Date>(new Date());
    const [pendingChanges, setPendingChanges] = useState<AffordableUtsvMonthValueInput[]>([]);
    const versionType = selectedTab === WORKFLOW_YEAR_TAB_BUDGET ? VersionType.Budget : VersionType.Reforecast;

    useEffect(() => {
        const updateWorkflowTab = shouldUpdateWorkflowTab(type, selectedTab);
        if (updateWorkflowTab != false) {
            setSelectedTab(updateWorkflowTab as string);
        }
    }, [type]);

    useEffect(() => {
        if(!currentProperty) {
            return;
        }

        getForecastLocks({
            variables: {
                propertyId: currentProperty.id,
                budgetYear: props.budgetYear
            }
        });
    }, [currentProperty, props.budgetYear]);

    useEffect(() => {
        if(currentProperty) {
            getAffordableUtsvData({
                variables: {
                    propertyId: currentProperty.id,
                    propertyIdString: currentProperty.id,
                    budgetYear: props.budgetYear,
                    versionType: versionType
                }
            });
        }
    }, [currentProperty, props.budgetYear, selectedTab]);

    useEffect(() => {
        if(loading || !data || !currentProperty) {
            return;
        }

        const parentRowId = "total";
        const totals: number[] = new Array(12).fill(0);
        const rows: IVZHotTableRow[] = [];
        for(const val of data.affordableUTSVData) {
            const tmp = val[props.field].map(n => n ?? 0);
            for(let i = 0; i < totals.length && i < tmp.length; i++) {
                totals[i] += tmp[i]!;
            }
            rows.push({
                parentRowId: parentRowId,
                rowId: val.unitTypeId,
                label: val.unitTypeName,
                values: [...tmp, 0],
                rowType: "child",
                rowSummaryFunction: "sum"
            });
        }
        rows.unshift({
            rowId: parentRowId,
            label: versionType === VersionType.Reforecast ? `Reforecast ${props.budgetYear - 1}` : `Budget ${props.budgetYear}`,
            values: totals,
            rowType: "summary",
            rowSummaryFunction: "sum",
            parentRowSummaryFunction: "sum"
        });

        const builtConfig = buildUTSVTableConfig(rows, false, versionType, currentProperty.reforecastStartMonthIndex ?? 0, props.format, "sum");
        builtConfig.applyToRow(0, {
            isReadOnly: true
        });
        setTableData(rows);
        setTableConfig(builtConfig);
    }, [currentProperty, data, loading, props.budgetYear]);

    useEffect(() => {
        if(pendingChanges.length === 0 || upsertAffordableUtsvDataLoading || !currentProperty) {
            return;
        }

        const localPending = pendingChanges;
        setPendingChanges([]);
        setSavingValues(prev => prev + 1);

        // Collapse the changes based on order.
        const collapsed = new Map<string, AffordableUtsvMonthValueInput>();
        for(const change of localPending) {
            const key = `${change.unitTypeId}-${change.monthIndex}`;
            const existing = collapsed.get(key);
            if(existing) {
                collapsed.set(key, {
                    ...existing,
                    [props.field]: props.field === "affordableModelAffordableUnitAvgMaxRent" ? change[props.field].toString() : change[props.field]
                });
            } else {
                if(props.field === "affordableModelAffordableUnitAvgMaxRent") {
                    change[props.field] = change[props.field].toString();
                }
                collapsed.set(key, change);
            }
        }

        const collapsedArray = Array.from(collapsed.values());
        upsertAffordableUtsvData({
            variables: {
                propertyId: currentProperty.id,
                budgetYear: props.budgetYear,
                versionType: versionType,
                data: collapsedArray
            }
        }).then(res => {
            if(res.data) {
                setLastSaved(new Date());
            }
        }).finally(() => {
            setSavingValues(prev => prev - 1);
        });
    }, [pendingChanges, upsertAffordableUtsvDataLoading]);

    function handleChanges(changes: [number, number, number][]) {
        const transformed: AffordableUtsvMonthValueInput[] = [];
        for(const [rowIndex, monthIndex, value] of changes) {
            const row = tableData[rowIndex];
            if(row && row.rowId) {
                transformed.push({
                    unitTypeId: row.rowId,
                    monthIndex: monthIndex,
                    [props.field]: value
                });
            }
        }
        setPendingChanges([...pendingChanges, ...transformed]);
    }

    return <ThemeProvider theme={ViziblyTheme}>
        <div className={`${workflowCSS.workflowContainer} ${workflowCSS.revenueScreen}`}>
            <WorkflowHeader>
                <WorkflowHeader.LeftCol>
                    <div className={workflowHeaderCSS.rowItemsContainer}>
                        <WorkflowNavToggle inUserWorkflow={false}/>
                        <WorkflowPageLabel label={props.pageName}/>
                    </div>
                </WorkflowHeader.LeftCol>
                <WorkflowHeader.RightCol>
                    <VZSaveStatus isSaving={savingValues !== 0 || pendingChanges.length > 0} lastSaved={lastSaved} />
                    <WorkflowStepNav onPreviousClick={props.onPreviousClick} onNextClick={props.onNextClick} inUserWorkflow={false}/>
                </WorkflowHeader.RightCol>
            </WorkflowHeader>
            <div className={workflowCSS.bodyContainer}>
                { currentProperty &&
                        <>
                            <Tabs selectedItem={selectedTab} onChange={t => setSelectedTab(t)} className={workflowCSS.tabsContainer}>
                                <WorkflowYearTabs
                                        property={currentProperty}
                                        selectedTab={selectedTab}
                                        rawForecastLocks={forecastLocks}
                                />
                            </Tabs>

                            <div className={workflowCSS.tableWrapper}>
                                <OperationalVZHotTable
                                        data={tableData}
                                        colWidths={[80]}
                                        colHeaders={["Year", ...getMonths(currentProperty), "Count"]}
                                        fixedRowsTop={1}
                                        fixedRowsBottom={tableData.filter(x => x.rowType == "historical").length}
                                        tableConfig={tableConfig}
                                        isDataLoading={loading}
                                        isSearchable
                                        isUndoRedoable
                                        summaryColumns={[
                                            {
                                                col: 13,
                                                summaryFunction: "sum",
                                            },
                                        ]}
                                        onChange={handleChanges}
                                />
                            </div>
                        </>
                }
            </div>
        </div>
    </ThemeProvider>;
}